import React, { useMemo } from 'react'
import { Route, Routes, Navigate } from 'react-router-dom' // Removed BrowserRouter
import { LoginCallback } from '@okta/okta-react'
import { ToastContainer } from 'react-toastify'
import { Helmet } from 'react-helmet'
import { connect } from 'react-redux'

import AppInitializer from './AppInitializer'
import { CustomOktaError } from './auth/components/CustomOktaError';
import Header from './components/Header'
import Alert from './layout/containers/Alert'
import Footer from './layout/components/Footer'
import UserManagement from './routes/UserManagement'
import RequestAccess from './routes/SignUp/View/RequestAccess'
import { Brokerages, BrokerageDetail } from './brokerages'
import { RequiredAuth, SecurityProvider } from './components/Security'
import Logout from './components/Logout'
import Signup from './auth/components/Signup'
import LoginTrouble from './auth/components/LoginTrouble'
import FAQ from './auth/components/FAQ'
import CompleteInvite from './auth/containers/CompleteInvite'
import CompleteProfile from './auth/containers/CompleteProfile'
import { SubmissionsHomeContainer } from './submissions'
import Sub_id from './submissions/Sub_id'
import Form from './routes/Form'
import FormResults from './routes/FormResults'
import { ClearanceContainer } from './clearance'
import PassClearance from './routes/PassClearance'
import FailClearance from './routes/FailClearance'
import ZigiZone from './routes/ZigiZone'
import withUserMustCompleteProfile from './auth/components/withUserMustCompleteProfile'
import SiteMaintainance from '../app/siteMaintainance'
import routes from './constants/routes'
import NewLogin from './auth/components/NewLogin'
import { isAuthenticated } from './auth/selectors'

const titleTemplate = `Owner's Edge | %s`
const meta = [
  { name: 'description', content: 'A digitized submission portal' },
  { property: 'og:type', content: 'site' },
]

const Protected = ({ component: Component }) => {
  const Wrapped = useMemo(() => withUserMustCompleteProfile(Component), [Component])
  return (
    <RequiredAuth>
      <Wrapped />
    </RequiredAuth>
  )
}

const App = ({ maintenance, isAuthenticated }) => {
  if (maintenance) return <SiteMaintainance />

  // NOTICE: <BrowserRouter> is gone from here.
  // It now lives in AppProviders.js as <HistoryRouter>
  return (
    <SecurityProvider>
      <AppInitializer>
        <div className="app">
          <div className="wrapper">
            <Helmet titleTemplate={titleTemplate} meta={meta} />
            <Header />
            <ToastContainer />
            <Alert />

            <Routes>
              <Route
                path={routes.ROOT}
                element={
                  isAuthenticated ? (
                    <Navigate to={routes.SUBMISSIONS} />
                  ) : (
                    <Navigate to={`${routes.LOGIN}?redirect=${routes.SUBMISSIONS}`} />
                  )
                }
              />
              <Route path={routes.IMPLICIT_CALLBACK} element={<LoginCallback errorComponent={CustomOktaError}/>} />
              <Route path={routes.TOKEN} element={<Navigate to={routes.LOGIN} />} />
              <Route path={routes.LOGIN} element={<NewLogin />} />
              <Route path={routes.LOGIN_CALLBACK} element={<LoginCallback errorComponent={CustomOktaError}/>} />
              <Route path={routes.SIGNUP} element={<Signup />} />
              <Route path={routes.LOGIN_TROUBLE} element={<LoginTrouble />} />

              <Route path={routes.FAQ} element={<Protected component={FAQ} />} />
              <Route path={routes.GET_PDF} element={<Protected component={Sub_id} />} />
              <Route path={routes.REQUEST_ACCESS} element={<RequestAccess />} />
              <Route path={routes.COMPLETE_INVITE} element={<CompleteInvite />} />
              <Route
                path={routes.COMPLETE_PROFILE}
                element={
                  <RequiredAuth>
                    <CompleteProfile />
                  </RequiredAuth>
                }
              />
              <Route
                path={routes.USERS}
                element={<Protected component={UserManagement} />}
              />
              <Route
                path={routes.BROKERAGE}
                element={<Protected component={BrokerageDetail} />}
              />
              <Route
                path={routes.BROKERAGES}
                element={<Protected component={Brokerages} />}
              />
              <Route
                path={routes.CLEARANCE}
                element={<Protected component={ClearanceContainer} />}
              />
              <Route
                path={routes.SUBMISSIONS}
                element={<Protected component={SubmissionsHomeContainer} />}
              />
              <Route path={routes.SUBMISSION} element={<Protected component={Form} />} />
              <Route
                path={routes.QUOTE}
                element={<Protected component={FormResults} />}
              />

              <Route path={routes.PASS_CLEARANCE} element={<PassClearance />} />
              <Route path={routes.FAIL_CLEARANCE} element={<FailClearance />} />

              <Route
                path={routes.SUBMISSION_LEGACY}
                element={<Navigate to={routes.SUBMISSION} />}
              />
              <Route
                path={routes.QUOTE_LEGACY}
                element={<Navigate to={routes.QUOTE} />}
              />

              <Route
                path={routes.ZIGIZONE}
                element={
                  <RequiredAuth>
                    <ZigiZone />
                  </RequiredAuth>
                }
              />
              <Route path={routes.LOGOUT} element={<Logout />} />
              <Route path="*" element={<NewLogin />} />
            </Routes>
          </div>
          <Footer />
        </div>
      </AppInitializer>
    </SecurityProvider>
  )
}

const mapStateToProps = (state) => ({
  isAuthenticated: isAuthenticated(state),
})

export default connect(mapStateToProps)(App)
